import { router, usePage } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";

import FaqCardComponent from "@/components/Cards/FaqComponent";
import Layout from "@/components/Layout";

import DividendsGraphSection from "./sections/dividendsGraphSection";
import DocsTableSection from "./sections/docsTableSection";
import QuotasSection from "./sections/quotasSection";
import SummarySection from "./sections/summarySection";

export default function InvestorIndex({
  shares,
  events,
  all_share_quantity,
  all_share_initial_investment,
  all_share_total_value,
  all_share_construction_appreciation,
  all_share_ipca_appreciation,
  last_dividend,
  dividend_yield,
  graph_data,
  graph_months,
  events_pagy,
}) {
  const { props } = usePage();
  const urlParams = new URLSearchParams(window.location.search);

  // Independent filters for Summary and Graph sections
  const [currentResumeYear, setCurrentResumeYear] = useState(
    urlParams.get("resume_year") || "Atual",
  );
  const [currentDividendFilter, setCurrentDividendFilter] = useState(
    urlParams.get("dividend_year") === "last_12_months"
      ? "Últimos 12 meses"
      : urlParams.get("dividend_year") || "Últimos 12 meses",
  );

  const [eventPage, setEventPage] = useState(events_pagy.page);
  const maxEventPages = events_pagy.pages;
  const isInitialRender = useRef(true);

  const resumeYears = () => {
    const startYear = 2024; // Investimentos começam em 2024
    const currentYear = new Date().getFullYear();
    return [
      ...Array.from({ length: currentYear - startYear + 1 }, (_, index) => startYear + index),
      "Atual",
    ];
  };

  const graphFilters = () => {
    const startYear = 2024; // Investimentos começam em 2024
    const currentYear = new Date().getFullYear();

    // Get complete years in ascending order
    const completeYears: string[] = [];
    for (let year = startYear; year <= currentYear; year++) {
      completeYears.push(year.toString());
    }

    // Add "Últimos 12 meses" at the end
    return [...completeYears, "Últimos 12 meses"];
  };

  const getEvents = (page) => {
    router.reload({
      only: ["events"],
      data: { event_page: page },
    });
  };

  useEffect(() => {
    // The server already rendered the data for the filters in the initial URL.
    // Reload only after the user changes one of the filters; otherwise an
    // Inertia remount can trigger this effect repeatedly for the same URL.
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const resumeParam = currentResumeYear === "Atual" ? null : currentResumeYear;
    const dividendParam =
      currentDividendFilter === "Últimos 12 meses" ? "last_12_months" : currentDividendFilter;

    router.reload({
      data: {
        resume_year: resumeParam,
        dividend_year: dividendParam,
      },
    });
  }, [currentResumeYear, currentDividendFilter]);

  return (
    <Layout>
      <div className="flex w-full flex-col justify-center px-8 py-32 md:px-16">
        <SummarySection
          allShareQuantity={all_share_quantity}
          allShareInitialInvestment={all_share_initial_investment}
          allShareTotalValue={all_share_total_value}
          allShareConstructionAppreciation={all_share_construction_appreciation}
          allShareIpcaAppreciation={all_share_ipca_appreciation}
          lastDividend={last_dividend}
          dividendYield={dividend_yield}
          years={resumeYears()}
          currentYear={currentResumeYear}
          setCurrentYear={setCurrentResumeYear}
          investorName={(props as any).investor_name}
        />
        <QuotasSection shares={shares} />
        <DividendsGraphSection
          data={graph_data}
          months={graph_months}
          filters={graphFilters()}
          currentFilter={currentDividendFilter}
          setCurrentFilter={setCurrentDividendFilter}
        />
        <DocsTableSection
          events={events}
          page={eventPage}
          setPage={setEventPage}
          maxPages={maxEventPages}
          getData={getEvents}
        />
        <div className="pt-12">
          <FaqCardComponent variant="questionSection" />
        </div>
      </div>
    </Layout>
  );
}
